// WAAP "Dashboard" — native (non-Superset) analytics. The BE pre-normalizes the
// account's BI overview into a list of typed chart widgets:
//   GET /dashboard/charts → { charts: WaapChart[] }
// Each widget is self-describing (discriminated on `kind`) so the FE can render
// it generically. A per-widget `error` string means that single chart failed to
// build server-side; the rest of the dashboard still renders.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { waapKeys, waapPath, WAAP_AREAS } from './_shared';

/** The Dashboard board is a fixed 30-day account overview (BE `whereCDN30d`). */
export const WAAP_DASHBOARD_WINDOW_DAYS = 30;

/** Number-formatting hint the BE attaches to numeric widgets. */
export type WaapChartFormat = '.3s' | 'SMART_NUMBER' | ',d' | string;

/** A pie slice (name → value). */
export interface WaapPieSlice {
  name: string;
  value: number;
}

/** Column spec for a `table` widget. `kind` drives cell formatting. */
export interface WaapTableColumn {
  key: string;
  label: string;
  /** text → raw, number → thousands, percent → fraction 0..1 rendered as "%". */
  kind: 'text' | 'number' | 'percent';
}

/** One row of a `table` widget, keyed by the column `key`s. */
export type WaapTableRow = Record<string, string | number | null>;

/** A `<Chart>`-ready series descriptor for a `timeseries` widget. */
export interface WaapTimeseriesSeries {
  key: string;
  label: string;
}

interface WaapChartBase {
  key: string;
  title: string;
  /** Non-empty when this single widget failed to build server-side. */
  error: string;
}

/** A single scalar KPI (e.g. "Peak Bandwidth"). */
export interface WaapNumberChart extends WaapChartBase {
  kind: 'number';
  value: number | null;
  /** "bps" → bits/sec, "B" → bytes, "" → plain number. */
  unit: string;
  format: WaapChartFormat;
}

/** A pie / donut breakdown. (`slices` is omitted by the BE when empty.) */
export interface WaapPieChart extends WaapChartBase {
  kind: 'pie';
  slices?: WaapPieSlice[];
}

/** A ranked table (e.g. "Top 10 Threat Source IP"). (`columns`/`rows` omitted when empty.) */
export interface WaapTableChart extends WaapChartBase {
  kind: 'table';
  columns?: WaapTableColumn[];
  rows?: WaapTableRow[];
}

/** A multi-series time series, ready for `<Chart>`. (`series`/`data` omitted when empty.) */
export interface WaapTimeseriesChart extends WaapChartBase {
  kind: 'timeseries';
  unit: string;
  format: WaapChartFormat;
  xKey: string;
  series?: WaapTimeseriesSeries[];
  data?: Record<string, unknown>[];
}

/** Discriminated union over `kind` of any dashboard widget. */
export type WaapChart =
  | WaapNumberChart
  | WaapPieChart
  | WaapTableChart
  | WaapTimeseriesChart;

/** GET /dashboard/charts response — the ordered list of widgets to render. */
export interface WaapChartsResponse {
  charts: WaapChart[];
}

/** Fetch + normalize-error the dashboard widgets. `refresh` bypasses the BE's 8h
 *  cache (evict + re-pull). Returns null when the dashboard is not configured
 *  server-side (400/404/501) so the page can show a "not configured" state. */
async function fetchDashboardCharts(
  domainId: string,
  opts?: { refresh?: boolean },
): Promise<WaapChartsResponse | null> {
  try {
    const params = new URLSearchParams({ tz: Intl.DateTimeFormat().resolvedOptions().timeZone });
    if (opts?.refresh) params.set('refresh', 'true');
    const res = await serviceApiV3.get<WaapChartsResponse>(
      `${waapPath(domainId, WAAP_AREAS.dashboardCharts)}?${params.toString()}`,
    );
    return res.data;
  } catch (e) {
    const status = (e as { response?: { status?: number } })?.response?.status;
    if (status === 400 || status === 404 || status === 501) return null;
    throw e;
  }
}

/** Read the normalized dashboard widgets for a website's account. */
export function useWaapDashboardCharts(domainId: string | undefined) {
  return useQuery({
    queryKey: waapKeys.aspect(domainId ?? '', WAAP_AREAS.dashboardCharts),
    enabled: !!domainId,
    retry: false,
    // BI aggregates move slowly; keep the widgets fresh for a minute so
    // re-navigating back to the Dashboard renders instantly (no refetch). The BE
    // response cache absorbs the rest. See waap_charts_cache.go.
    staleTime: 60_000,
    queryFn: () => fetchDashboardCharts(domainId as string),
  });
}

/** Force a fresh dashboard pull — the "Refresh" button. Bypasses the BE 8h cache
 *  and writes the result straight into the query cache so the widgets update
 *  without a second fetch. */
export function useRefreshWaapDashboardCharts(domainId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => fetchDashboardCharts(domainId as string, { refresh: true }),
    onSuccess: (data) => {
      qc.setQueryData(waapKeys.aspect(domainId ?? '', WAAP_AREAS.dashboardCharts), data);
    },
  });
}
